import { createEffect, createResource, For, JSX, Show, Suspense } from "solid-js";
import { requireVisitorCheckinContext } from "../VisitorCheckinContext";
import { createProxySignal, IPrinter, toast, visitorManagementClient } from "common";
import { visitorCheckin as api } from 'st-app/api'
import styles from './print-step.module.css'
import Select from '../../st-select/st-select';
import Loader from '../../st-loader/st-loader';
import Button from '../../st-button/st-button';
import STInput from '../../st-input/st-input';
import StaffSearchInput from "./staff-search-input";
import Textarea from '../../st-textarea/st-textarea';
import { formatDob, formatName } from "./util";

// Mirrors the product-owned VISITOR_TYPE lookup. Every code in that lookup MUST appear here:
// this map is the label source for the badge's VisitorType text (createEffect below), the Visitor
// Type <Select> options, and the recent-visits list. A missing code falls through to the raw code,
// so an unlisted value prints "3" on the badge and leaves the Select with no matching option -
// which would blank the type when an operator saves.
//
// '3' (Staff) is required specifically because an Ambiguous staff badge scan is stored REVW, and
// PushReviewPayload hydrates that row into this wizard (VisitorCheckinContext.tsx:265) for an
// operator to resolve. The row must stay typed Staff through that resolution or the employee drops
// off the emergency roster.
const VisitorTypeLookup = {
    '0': 'Parent',
    '1': 'Visitor',
    '2': 'Vendor',
    '3': 'Staff',
}

/**
 * VISITOR_TYPE lookup code 3 = Staff. Named so the staff-row test below reads as intent.
 * Exported so st-visitor-checkin.tsx's submit() can gate the print step on the same code
 * rather than repeating the '3' literal.
 */
export const VISITOR_TYPE_STAFF = '3';

const REASON_OTHER = '6';

export const PrintStep = () => {
    const checkin = requireVisitorCheckinContext();

    /**
     * A staff row: either an ambiguous staff badge stored REVW by the kiosk and reopened via
     * [Review], OR an ordinary walk-in where the operator picked "Staff" from the Visitor Type
     * dropdown in normal Add mode.
     *
     * Staff check-ins are recorded for emergency accountability with NO printed badge, so a printer
     * must never be offered for one — regardless of which path produced the Staff type. submit() in
     * st-visitor-checkin.tsx independently skips the print call itself when visitorType() is Staff;
     * this condition just keeps the UI honest about that by hiding the printer picker and showing
     * "No badge needed" instead of a control that would do nothing.
     *
     * DEFENCE IN DEPTH for the review path specifically — that step is not reachable there anyway.
     * The staff candidate picker on the Verification step is terminal: it calls CompleteStaffCheckIn
     * (which records ALLOW itself) and closes the wizard, and while it is on screen verificationStatus
     * stays 'pending', which both ensureVerified and submit() refuse. This exists so that a staff row
     * can never show a printer even if some future path does arrive here.
     *
     * TWO TERMS, on purpose, and they are not interchangeable:
     *
     *  - reviewVisitorType() is the type the ROW was hydrated with, captured immutably in
     *    hydrateForReview. It is what makes a genuine kiosk staff row a staff row, and an operator
     *    cannot edit it away. Verification's isStaffRow() routes on this value ALONE, because routing
     *    on an operator-editable signal stranded them on a dead loading panel.
     *  - visitorType() is the editable value, and it is the one submit() tests before printing
     *    (`visitorType() !== VISITOR_TYPE_STAFF`). It has to stay in this condition so that an Add-mode
     *    walk-in typed "Staff" still shows "No badge needed" instead of a printer picker that submit()
     *    is about to ignore.
     *
     * Keeping both makes this notice appear in exactly the cases where submit() will skip the print,
     * plus the genuine staff row where nothing prints regardless. It is never possible to reach the
     * printer picker on a real staff row by editing the dropdown.
     */
    const isStaffRow = () => checkin.reviewVisitorType() === VISITOR_TYPE_STAFF
        || checkin.visitorType() === VISITOR_TYPE_STAFF;

    const [printers, { refetch: refreshPrinters }] = createResource<IPrinter[]>(async () => {
        try {
            const available = await visitorManagementClient.listPrinters();
            if (available.length && !checkin.selectedPrinterName()) {
                checkin.setSelectedPrinterName(available[0].name)
            }

            return available;
        } catch (e) {
            console.error(e);
            toast.notify('error', 'An error occurred while loading the list of available printers')
        }

        return []
    })

    const [reasonLookup] = createResource(async () => {
        return api.GetReasonLookup();
    })

    createEffect(() => {
        const t = checkin.visitorType();
        if (t) {
            checkin.setPrintData('VisitorType', VisitorTypeLookup[t as keyof typeof VisitorTypeLookup] ?? t);
        }
    })

    createEffect(() => {
        const r = checkin.reason();
        const lookup = reasonLookup();
        if (r) {
            checkin.setPrintData('Reason', lookup ? lookup[r] : r);
        }
    })

    const visitorTypeProxy = createProxySignal(() => checkin.visitorType() as string, (t) => checkin.setVisitorType(t as keyof typeof VisitorTypeLookup));

    return (
        <div class={styles['print-step']}>
            <Show when={checkin.repeatTier() === 'Remembered'}>
                <div class={styles['welcome-banner']}>
                    <i class="fas fa-circle-check" />
                    <span>
                        <strong>Welcome back{checkin.scan()?.AAMVA?.firstName ? `, ${checkin.scan()?.AAMVA?.firstName}` : ''}.</strong>
                        {' '}Pre-filled from the last visit — tap Complete to reissue the badge, or edit any field first.
                    </span>
                </div>
            </Show>

            <div class={styles['identity-card']}>
                <div class={styles['print-step-visitor-avatar']}>
                    <img src={`data:image/bmp;base64,${checkin.scan()?.photoBase64}`}></img>
                </div>
                <div class={styles['visitor-info']}>
                    <Field label="Full Name:" value={formatName(checkin.scan()?.AAMVA)} />
                    <Field label="Date of Birth:" value={formatDob(checkin.scan()?.AAMVA?.dateOfBirth)} />
                    <Field label="License Number:" value={checkin.scan()?.AAMVA?.idNumber} />
                    <Field label="Issuing State:" value={checkin.scan()?.AAMVA?.issuerIdentificationNumber} />
                </div>
            </div>

            <div class={styles['form-col']}>
                <Show when={isStaffRow()} fallback={
                    <div class={styles['printer-row']}>
                        <label class={styles['field-label']}>
                            Printer
                            <Suspense fallback={<Loader />}>
                                <Show when={!printers.loading}>
                                    <Select value={[checkin.selectedPrinterName, checkin.setSelectedPrinterName]}>
                                        <For each={printers()}>{(printer) => (
                                            <Select.Option value={printer.name}><span>{printer.name}</span></Select.Option>
                                        )}</For>
                                    </Select>
                                </Show>
                            </Suspense>
                        </label>
                        <Button onClick={refreshPrinters} icon='fas fa-arrows-rotate' title='Refresh Printer List' />
                    </div>
                }>
                    <div style='display:flex; gap:12px; align-items:flex-start; padding:12px 16px; border-radius:6px; background:#e7f4e8; border:1px solid #1f8b3b; color:#14571f;'>
                        <i class='fas fa-circle-check' style='font-size:1.4em; margin-top:2px;' />
                        <span>
                            <strong>No badge needed.</strong>{' '}
                            Staff check-ins are recorded for emergency accountability only — nothing is printed.
                        </span>
                    </div>
                </Show>

                <div class={styles['form-grid']}>
                    <label class={styles['field-label']}>
                        Reason
                        <Suspense fallback={<Loader />}>
                            <Select required value={[checkin.reason, checkin.setReason]}>
                                <For each={Object.entries(reasonLookup() ?? {})}>{([key, value]) => (
                                    <Select.Option value={key}><span>{value}</span></Select.Option>
                                )}</For>
                            </Select>
                        </Suspense>
                    </label>
                    <label class={styles['field-label']}>
                        Visitor Type
                        <Select required value={visitorTypeProxy}>
                            <For each={Object.entries(VisitorTypeLookup)}>{([key, value]) => (
                                <Select.Option value={key}><span>{value}</span></Select.Option>
                            )}</For>
                        </Select>
                    </label>
                    <label class={`${styles['field-label']} ${styles['span-2']}`}>
                        Meeting With
                        <StaffSearchInput value={[checkin.meetingWith, checkin.setMeetingWith]} displayName={[checkin.meetingWithName, checkin.setMeetingWithName]} />
                    </label>
                    <STInput label='Cell Phone' value={[checkin.cellPhone, checkin.setCellPhone]} />
                    <STInput label='Email' value={[checkin.email, checkin.setEmail]} />
                    <div class={styles['span-2']}>
                        <Textarea label='Notes' value={[checkin.notes, checkin.setNotes]} rows={2} required={checkin.reason() === REASON_OTHER} />
                    </div>
                </div>

                <Show when={checkin.history().length > 0}>
                    <details open class={styles['history-collapse']}>
                        <summary>Recent visits ({checkin.history().length}) — tap one to reuse its details</summary>
                        <div class={styles['history-list']}>
                            <For each={checkin.history()}>{(entry) => (
                                <div class={styles['history-entry']} onClick={() => checkin.loadFromHistoryEntry(entry)}>
                                    <div class={styles['history-entry-line']}><strong>Reason:</strong><span>{reasonLookup()?.[entry.reason] || entry.reason}</span></div>
                                    <div class={styles['history-entry-line']}><strong>Type:</strong><span>{VisitorTypeLookup[entry.visitorType as keyof typeof VisitorTypeLookup] || entry.visitorType}</span></div>
                                    <div class={styles['history-entry-line']}><strong>Notes:</strong><span>{entry.notes}</span></div>
                                </div>
                            )}</For>
                        </div>
                    </details>
                </Show>
            </div>
        </div>
    )
}

const Field = (props: { label: string; value: string | JSX.Element | undefined }) => {
    return (
        <div class={styles['field']}>
            <span class={styles['label']}>{props.label}</span>
            <span class={styles['value']}>{props.value ?? 'N/A'}</span>
        </div>
    )
}
